/**
 * CalculationEditor — configure a calculation question (WCR parity): pick a
 * preset (age / BMI) and map its input slots to compatible questions. Slots
 * may stay unmapped (the server skips unmapped inputs, like WCR).
 */
import { FormQuestionDef } from '../../types';
import { CALC_PRESETS, CALC_PRESET_KEYS } from './formMeta';

interface CalcConfig { preset: string; inputs: Record<string, string>; }

interface CalculationEditorProps {
  config: CalcConfig | null | undefined;
  /** Questions that can feed a slot (caller excludes self + calculations). */
  questions: FormQuestionDef[];
  onChange: (config: CalcConfig) => void;
}

const selectCls =
  'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

export default function CalculationEditor({ config, questions, onChange }: CalculationEditorProps) {
  const preset = config?.preset && CALC_PRESETS[config.preset] ? config.preset : '';
  const meta = preset ? CALC_PRESETS[preset] : null;

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Calculation Type</label>
        <select
          value={preset}
          onChange={(e) => onChange({ preset: e.target.value, inputs: {} })}
          className={selectCls}
        >
          <option value="" disabled>Select a calculation</option>
          {CALC_PRESET_KEYS.map((p) => <option key={p} value={p}>{CALC_PRESETS[p].label}</option>)}
        </select>
        {meta && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{meta.description}</p>}
      </div>

      {meta && (
        <div className="space-y-3">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Map Input Questions</span>
          {meta.inputs.map((slot) => {
            const compatible = questions.filter((q) => slot.accept.includes(q.type));
            const selected = config?.inputs?.[slot.name] || '';
            // A slot pointing at the SAME question as another slot is almost
            // always a mis-wire — e.g. a BMI with both height and weight mapped
            // to the weight question reads the weight as a height (the
            // 2026-08-13 bug). The publish gate rejects it too; warn here first.
            const duplicate = Boolean(selected) && meta.inputs.some(
              (other) => other.name !== slot.name && config?.inputs?.[other.name] === selected,
            );
            return (
              <div key={slot.name}>
                <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">{slot.label}</label>
                <select
                  value={selected}
                  onChange={(e) => onChange({ preset: config?.preset || preset, inputs: { ...config?.inputs, [slot.name]: e.target.value } })}
                  className={selectCls}
                >
                  <option value="">— pick a question —</option>
                  {compatible.map((q) => <option key={q.key} value={q.key}>{q.label || q.key}</option>)}
                </select>
                {compatible.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    No compatible questions yet — add a {slot.accept.join(' or ')} question first.
                  </p>
                )}
                {duplicate && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    This is mapped to the same question as another input — each input should point at its own question.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
